import {
  PUBLIC_TIER_LABELS,
  PUBLIC_TIER_MONTHLY_PRICES_AUD,
  PUBLIC_TIER_PRICE_IDS,
  type PublicTier,
} from "./subscriptionPlans.ts";

export const HOSTING_BASE_PLAN = "hosting_basic" as const;
export type SecurityAddonTier = Exclude<PublicTier, "basic">;

export type DomainPlanShape = {
  tier?: string | null;
  stripe_price_id?: string | null;
  security_addon_tier?: string | null;
  security_addon_price_id?: string | null;
  stripe_subscription_id?: string | null;
  security_addon_subscription_id?: string | null;
};

export const BASIC_HOSTING_MONTHLY_PRICE_AUD = PUBLIC_TIER_MONTHLY_PRICES_AUD.basic;

export const isSecurityAddonTier = (value: string | null | undefined): value is SecurityAddonTier =>
  value === "essential" || value === "premium";

export const isHostingBaseTier = (value: string | null | undefined) => value === "basic";

export const getBasePlanLabel = (domain: DomainPlanShape) => {
  if (domain.tier === "dev") return "Reviewed pricing";
  if (domain.tier === "basic" || domain.tier === "essential" || domain.tier === "premium") {
    return PUBLIC_TIER_LABELS[domain.tier];
  }
  return "Premium";
};

export const getEffectiveSecurityTier = (domain: DomainPlanShape): SecurityAddonTier | null => {
  if (isSecurityAddonTier(domain.security_addon_tier)) return domain.security_addon_tier;
  if (isSecurityAddonTier(domain.tier)) return domain.tier;
  return null;
};

export const hasHostingBase = (domain: DomainPlanShape) => isHostingBaseTier(domain.tier);

export const hasManagedSecurityAddon = (domain: DomainPlanShape) =>
  hasHostingBase(domain) && isSecurityAddonTier(domain.security_addon_tier);

export const getCombinedPlanLabel = (domain: DomainPlanShape) => {
  if (domain.tier === "dev") return "Reviewed pricing";
  if (hasManagedSecurityAddon(domain)) {
    return `${PUBLIC_TIER_LABELS.basic} + ${PUBLIC_TIER_LABELS[domain.security_addon_tier as SecurityAddonTier]}`;
  }
  return getBasePlanLabel(domain);
};

export const getCombinedMonthlyPriceAud = (domain: DomainPlanShape) => {
  if (domain.tier === "dev") return null;
  if (hasManagedSecurityAddon(domain)) {
    return PUBLIC_TIER_MONTHLY_PRICES_AUD.basic +
      PUBLIC_TIER_MONTHLY_PRICES_AUD[domain.security_addon_tier as SecurityAddonTier];
  }
  if (domain.tier === "basic" || domain.tier === "essential" || domain.tier === "premium") {
    return PUBLIC_TIER_MONTHLY_PRICES_AUD[domain.tier];
  }
  return PUBLIC_TIER_MONTHLY_PRICES_AUD.premium;
};

export const getPrimaryPriceId = (domain: DomainPlanShape) => domain.stripe_price_id || null;

export const getSecurityAddonPriceId = (domain: DomainPlanShape) => {
  if (!isSecurityAddonTier(domain.security_addon_tier)) return null;
  return domain.security_addon_price_id || PUBLIC_TIER_PRICE_IDS[domain.security_addon_tier];
};

export const getCombinedSubscriptionId = (domain: DomainPlanShape) =>
  domain.security_addon_subscription_id || domain.stripe_subscription_id || null;
